import { GetProductRegime, ProductRegimeModel } from 'autogen/swagger/all';
import React, { ReactNode, useState, useRef, useEffect } from 'react';
import { IRegimeContext, SelectableProduct } from '../shared/types';

const RegimeContext = React.createContext({} as IRegimeContext);

export default function Regime({
    model,
    excludeMainProduct = false,
    children
}: {
    model: ProductRegimeModel;
    excludeMainProduct?: boolean;
    children: ReactNode;
}) {
    const initialRender = useRef(!excludeMainProduct);
    const { productId: mainProductRegimeId } = model;

    // Set the initial state to be all products selected.
    const initializeSelectedProducts = () =>
        model.products.map((product) => ({
            id: product.product.id,
            selected:
                (product.stockAvailable && !excludeMainProduct) ||
                (product.stockAvailable && mainProductRegimeId === product.product.id)
        }));

    // when we have model that is better tailored to regime, clean this up and dont pass the whole model ( the whole model is very big ).
    const [regimeModel, setRegimeModel] = useState<ProductRegimeModel>(model);
    const [selectableProducts, setSelectableProducts] = useState<SelectableProduct[]>(initializeSelectedProducts());

    const updateRegime = async () => {
        try {
            const allEntryIds = selectableProducts.map((product) => product.id);
            const excludedIds = selectableProducts.reduce<number[]>((results, product) => {
                if (!product.selected) results.push(product.id);

                return results;
            }, []);

            const updatedRegime = await GetProductRegime({
                productId: mainProductRegimeId,
                allProductIds: allEntryIds,
                deselectedIds: excludedIds
            });

            setRegimeModel(updatedRegime);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        updateRegime();
    }, []);

    // Run UpdateRegime everytime selectableProducts changes.
    useEffect(() => {
        if (initialRender.current) {
            initialRender.current = false;
            return;
        }
        updateRegime();
    }, [selectableProducts]);

    /* isSelected Method */
    const isSelected = (id) => selectableProducts.some((product) => product.id === id && product.selected);

    /* SelectProduct Method */
    const selectProduct = (id) =>
        !isSelected(id) &&
        setSelectableProducts((prevState) =>
            prevState.map((product) => (product.id === id ? { id: product.id, selected: true } : product))
        );

    /* DelesectProduct Method */
    const deselectProduct = (id) =>
        isSelected(id) &&
        setSelectableProducts((prevState) =>
            prevState.map((product) => (product.id === id ? { id: product.id, selected: false } : product))
        );

    /* ToggleProduct Method */
    const toggleProduct = (id) => (isSelected(id) ? deselectProduct(id) : selectProduct(id));

    /* ReplaceProduct Method */
    const replaceProduct = (idToReplace, newId) => {
        if (isSelected(newId)) return;

        const index = selectableProducts.findIndex((product) => product.id === idToReplace);

        if (index !== -1) {
            const newSelectedProducts = [...selectableProducts];
            newSelectedProducts[index] = { id: newId, selected: true };
            setSelectableProducts(newSelectedProducts);
            return;
        }

        selectProduct(newId);
    };

    /* Check to see if regime contains sold out product */
    const hasSoldOutProduct = () => {
        if (model.products.some((product) => !product.stockAvailable)) {
            return true;
        }

        return false;
    };

    /* Check to see if regime contains product with variant */
    const hasProductWithVariants = () => {
        if (model.products.some((product) => product.variants.length > 1)) {
            return true;
        }

        return false;
    };

    const hasSelectedProducts = () => selectableProducts.some((product) => product.selected);

    const getSelectedProducts = () =>
        selectableProducts.reduce<number[]>((results, product) => {
            if (product.selected) results.push(product.id);

            return results;
        }, []);

    let { products } = regimeModel;

    if (excludeMainProduct) {
        products = regimeModel.products.filter((x) => x.product.id !== model.productId);
    }

    const value: IRegimeContext = {
        mainProductRegimeId,
        selectableProducts,
        productEntries: products,
        isSelected,
        selectProduct,
        deselectProduct,
        toggleProduct,
        replaceProduct,
        hasSelectedProducts,
        getSelectedProducts,
        regimeModel,
        excludeMainProduct,
        hasSoldOutProduct,
        hasProductWithVariants
    };

    return <RegimeContext.Provider value={value}>{children}</RegimeContext.Provider>;
}

export function useRegime() {
    return React.useContext(RegimeContext);
}
